import { InjectionToken } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SVGIcon } from '@progress/kendo-svg-icons';
import { DrawerItem } from '@progress/kendo-angular-layout';
import { User } from '../../models/user';
import { WorkspaceId } from './workspaces';

export interface ExtendedDrawerItem extends DrawerItem {
  svgIcon?: SVGIcon;
  path?: string;
  selected?: boolean;
  external?: boolean;
  cssClass?: string;
}

export interface FooterConfig {
  showVipSupport: boolean;
  showResources: boolean;
  showNotifications: boolean;
  showSettings: boolean;
  showUserAvatar: boolean;
}

export interface ResourceLink {
  text: string;
  url: string;
  external: boolean;
}

export interface ScriptConfig {
  injectZoho: boolean;
  removeChatWidget: boolean;
}

export interface FlagGatedRoute {
  flag: string;
  pathPrefix: string;
}

export interface LayoutConfig {
  menuItemsFactory: (user: User, flags?: Record<string, boolean>) => ExtendedDrawerItem[];
  footer: FooterConfig;
  userMenuItemsFactory: (user: User) => Array<{ text: string; icon?: string }>;
  onOrgChange: (event: any, router: Router, route: ActivatedRoute) => void;
  settingsRoute: string | null;
  resourceLinks: ResourceLink[];
  scripts: ScriptConfig;
  onUserProfileAction?: (actionText: string, router: Router) => void;

  /** Enable CustomerFeatureFlagsService for reactive nav rebuilding and flag-gated route redirects. */
  enableFeatureFlags?: boolean;
  /** Routes that require a specific feature flag — used to redirect when a flag is disabled. */
  flagGatedRoutes?: ReadonlyArray<FlagGatedRoute>;
  /** Default route to redirect to when a flag-gated route is disabled. */
  defaultRedirectRoute?: string;
  /** Enable SupportWidgetService (Zoho/Support Bridge) instead of using scripts config directly. */
  enableSupportWidget?: boolean;
  /** Show the organization picker in the sidebar header. Defaults to true. */
  showOrgPicker?: boolean;
  /** Which area this layout is; the last page visited in it is remembered. */
  workspace?: WorkspaceId;
  /**
   * Frames the content as a separate space: a border plus a bar with the space's name
   * and, when the user has somewhere to go back to, a return button that lands on the
   * last page they had open there.
   */
  sandbox?: SandboxConfig;
}

export interface SandboxConfig {
  label: string;
  returnTo?: {
    label: string;
    workspace: WorkspaceId;
    /** Only users who have that area get the button. */
    available: (user: User) => boolean;
    /** Where to go when no page there is remembered. */
    home: (user: User) => string;
  };
}

export const LAYOUT_CONFIG = new InjectionToken<LayoutConfig>('LayoutConfig');
